import { Button, Card, Col, Row, message } from 'antd';
import Banner from 'components/QuickTurn/Banner';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ModelingDemo from '../../../assets/images/quick-turn/landingpage/modeling-demo.gif';

import FastTurnaroundIcon from '../../../assets/images/quick-turn/landingpage/fastTurnaroundIcon.svg';
import CostSavingsIcon from '../../../assets/images/quick-turn/landingpage/costSavingsIcon.svg';
import CustomizationIcon from '../../../assets/images/quick-turn/landingpage/customizationIcon.svg';
import HighQualityIcon from '../../../assets/images/quick-turn/landingpage/highQualityIcon.svg';
import ImprovedProductivityIcon from '../../../assets/images/quick-turn/landingpage/improvedProductivityIcon.svg';

import PartExample from '../../../assets/images/quick-turn/landingpage/part_example.svg';

import { MailOutlined, PhoneOutlined, CopyOutlined } from '@ant-design/icons';
import 'animate.css';
import UTMQueryPreservingLink from '../../../common/UTMQueryPreservingLink';

interface ICard {
    icon?: string;
    title: string;
    text: string;
}

const SmallCard: React.FC<ICard> = ({ icon, title, text }) => {
    return (
        <div className="flex flex-col items-center p-3">
            <img src={icon} alt="Modeling in the browser" style={{ width: '64px', opacity: '0.5' }} className="mb-2" />
            <h4 className="text-black font-bold mb-3">{title}</h4>
            <span className="text-center">{text}</span>
        </div>
    );
};

const BigCard: React.FC<ICard> = ({ icon, title, text }) => {
    return (
        <Col xl={6} lg={12} md={12} span={24}>
            <Card className="h-100 shadow-sm">
                <p className="text-xl text-black font-bold">{title}</p>
                <span>{text}</span>
            </Card>
        </Col>
    );
};

const LandingPage = () => {
    const benefits_texts = [
        {
            id: 1,
            icon: FastTurnaroundIcon,
            title: 'Fast turnaround times',
            text:
                'Our QuickTurn process allows us to produce parts quickly, often in just a few hours or days. This means you can get the parts you need when you need them, without having to wait weeks for traditional machining services.',
        },
        {
            id: 2,
            icon: HighQualityIcon,
            title: 'High-quality parts',
            text:
                'Using the latest technology and techniques to produce parts with exceptional accuracy and precision. This means you can rely on our parts to perform as expected and meet your quality standards.',
        },
        {
            id: 3,
            icon: CostSavingsIcon,
            title: 'Cost savings',
            text:
                'Saving you money by reducing the amount of time and labor required to produce parts. This means you can get the parts you need at a lower cost than traditional machining services.',
        },
        {
            id: 4,
            icon: CustomizationIcon,
            title: 'Customization',
            text:
                'Produce customized parts quickly and easily. This means you can get the exact parts you need, without having to compromise on design or functionality.',
        },
        {
            id: 5,
            icon: ImprovedProductivityIcon,
            title: 'Improved productivity',
            text:
                'Our QuickTurn process allows you to get the parts you need quickly, which means you can keep your production line running smoothly and avoid costly downtime.',
        },
    ];

    const features_texts = [
        {
            id: 1,
            title: 'Part Designer',
            text:
                'Our online Part Designer tool allows you to easily create and customize the parts you need. Simply input your design specifications, and our system will generate a 3D model for you to review and adjust as needed. This allows you to create custom parts quickly and easily without the need for specialized software or technical knowledge.',
        },
        {
            id: 2,
            title: 'Online Checkout',
            text:
                "Once you've designed your parts, you can quickly and easily place an order using our secure online checkout system. Our checkout process is simple and straightforward, allowing you to review your order and confirm your details before submitting your payment.",
        },
        {
            id: 3,
            title: 'Real-Time Order Tracking',
            text:
                "Once your order is placed, you can track its progress in real-time using our online order tracking system. This allows you to stay up-to-date on your order's status and estimated delivery date, so you can plan accordingly and keep your production line running smoothly.",
        },
        {
            id: 4,
            title: 'Customization',
            text:
                'Produce customized parts quickly and easily. This means you can get the exact parts you need, without having to compromise on design or functionality.',
        },
        // {
        //     id: 5,
        //     title: 'Expert Support',
        //     text:
        //         'Our team of technology and machining professionals is always available to provide support and answer any questions you may have about our QuickTurn process, Part Designer tool, or online checkout system.',
        // },
    ];

    async function copyToClipboard(info: string) {
        navigator.clipboard.writeText(info);

        message.success(`Copied the text: ${info}`);
    }

    function scrollTrigger(selector: string) {
        let els: any = document.querySelectorAll(selector);

        els = Array.from(els);
        els.forEach((el: Element) => {
            addObserver(el);
        });
    }
    function addObserver(el: any) {
        // We are creating a new IntersectionObserver instance
        const observer = new IntersectionObserver((entries, observer) => {
            // This takes a callback function that receives two arguments: the elements list and the observer instance.
            entries.forEach(entry => {
                // `entry.isIntersecting` will be true if the element is visible
                if (entry.isIntersecting) {
                    // We are removing the observer from the element after adding the animate class
                    entry.target.classList.remove('quickturn-landingpage-transition');

                    entry.target.classList.add('animate__animated', 'animate__backInUp');
                }
            });
        });
        // Adding the observer to the element
        observer.observe(el);
    }

    useEffect(() => {
        scrollTrigger('.quickturn-landingpage-transition');
    }, []);

    return (
        <>
            <Banner />
            <div className="border-bottom">
                <section className="bg-white border-bottom w-100 mb-3 p-5">
                    <div className="container">
                        <span className="text-lg">
                            Welcome to PartsBadger’s QuickTurn Machining! We are your go-to source for fast,
                            high-quality machining services. Our QuickTurn process is designed to provide you with the
                            speed, accuracy, and reliability you need to keep your business running smoothly.
                        </span>
                    </div>
                </section>
                <section className="container mb-5">
                    <div
                        className="d-flex flex-xl-row flex-column justify-between pt-2 pb-2 w-full"
                        style={{ columnGap: '4rem' }}
                    >
                        <div className="animate__animated animate__backInLeft quickturn-landingpage-gif mb-xl-0 mb-3 d-flex justify-content-center align-items-center">
                            <img
                                src={ModelingDemo}
                                alt="Modeling in the browser"
                                style={{ display: 'block', maxHeight: '480px', width: '100%', marginTop: '5%' }}
                            />
                        </div>
                        <div className="animate__animated animate__backInRight d-flex flex-column justify-center">
                            <span className="plus-jakarta-sans font-size-xxl font-weight-bold mb-3">
                                What is QuickTurn Machining?
                            </span>
                            <span className="d-block text-lg mb-4">
                                QuickTurn Machining is a cutting-edge process that allows us to produce high-quality
                                machined parts in record time. We use the latest technology and techniques to provide
                                you with the fastest, most accurate machining available.
                                {/* Our team of expert machinists
                                have years of experience and a deep understanding of the tools and techniques required
                                for QuickTurn machining. */}
                            </span>
                            <div>
                                <Button type="primary" size="large" className="font-weight-bold">
                                    <UTMQueryPreservingLink to="/quick-turn/design">Start Creating Your Custom Parts</UTMQueryPreservingLink>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="bg-white border-bottom border-top w-100 mb-5 p-5">
                    <div className="quickturn-landingpage-transition container">
                        <div>
                            <span className="d-block plus-jakarta-sans font-size-xxl font-weight-bold mb-3">
                                Benefits of QuickTurn Machining
                            </span>
                            <span className="d-block text-lg mb-5">
                                We understand the importance of speed and efficiency in today's fast-paced business
                                environment. That's why we've developed our QuickTurn process to provide you with a
                                range of benefits, including:
                            </span>
                        </div>
                        <div className="grid xl:grid-cols-5 lg:grid-cols-3 md:grid-cols-1">
                            {benefits_texts.map(item => (
                                <SmallCard key={item.id} icon={item.icon} title={item.title} text={item.text} />
                            ))}
                        </div>
                        <div className="flex justify-center">
                            <Button type="primary" className="font-weight-bold" size="large">
                                <UTMQueryPreservingLink to="/quick-turn/design">Launch QuickTurn Machining</UTMQueryPreservingLink>
                            </Button>
                        </div>
                    </div>
                </section>
                <section className="quickturn-landingpage-transition container mb-5">
                    {/* <div className="mb-5">
                        <span className="text-lg">
                            In addition to our fast and reliable QuickTurn Machining process, we also offer a range of
                            online tools to make the ordering process quick and easy. Here are some of the features we
                            offer:
                        </span>
                    </div> */}
                    <Row gutter={[16, 16]} justify="center" align="stretch">
                        {features_texts.map(item => (
                            <BigCard key={item.id} title={item.title} text={item.text} />
                        ))}
                    </Row>
                </section>
                <section className="quickturn-landingpage-transition container">
                    <div className="flex flex-col md:flex-row justify-between mb-5">
                        <div className="flex flex-col justify-center">
                            <span className="plus-jakarta-sans font-size-xxl font-weight-bold mb-3">
                                Why Choose QuickTurn Machining?
                            </span>
                            <span className="d-block text-lg mb-4">
                                We are dedicated to providing our customers with the highest level of service and
                                quality. Our QuickTurn process is designed to meet the needs of businesses in a wide
                                range of industries, including: aerospace, automotive, electronics, personal projects
                                and more. Whether you need a single prototype or a large production run, we have the
                                tools and expertise to get the job done.
                            </span>
                            <div>
                                <Button type="primary" className="font-weight-bold" size="large">
                                    <UTMQueryPreservingLink to="/quick-turn/design">Go To Part Designer</UTMQueryPreservingLink>
                                </Button>
                            </div>
                        </div>
                        <div className="quickturn-landingpage-img">
                            <img src={PartExample} alt="Modeling in the browser" style={{ maxHeight: '480px' }} />
                        </div>
                    </div>
                </section>
                {/* <section className="bg-white border-top w-100 p-5">
                    <div className="quickturn-landingpage-transition container mb-5">
                        <span className="plus-jakarta-sans font-size-xxl font-weight-bold mb-3">Contact Us Today</span>
                        <span className="d-block text-lg mb-4">
                            If you're looking for fast, reliable machining services, look no further than QuickTurn
                            Machining. Contact us today to learn more about our services and how we can help your
                            business succeed.
                        </span>
                        <div className="flex md:justify-center">
                            <div className="flex flex-col md:flex-row justify-between md:w-1/2">
                                <div className="flex mb-4">
                                    <span className="mr-2">
                                        <MailOutlined style={{ fontSize: 40 }} />
                                    </span>
                                    <div className="flex flex-col">
                                        <span className="font-bold">Email:</span>
                                        <span>quickturn@quick-turn.com</span>
                                    </div>
                                    <Button
                                        type="link"
                                        onClick={() => copyToClipboard('quickturn@quick-turn.com')}
                                        icon={<CopyOutlined />}
                                    />
                                </div>
                                <div className="flex justiy-center">
                                    <span className="mr-2">
                                        <PhoneOutlined style={{ fontSize: 40 }} />
                                    </span>
                                    <div className="flex flex-col">
                                        <span className="font-bold">Phone:</span>
                                        <span>(331) BADGER-8 or (331) 223-4378</span>
                                    </div>
                                    <Button
                                        type="link"
                                        onClick={() => copyToClipboard('(331) 223-4378')}
                                        icon={<CopyOutlined />}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}
            </div>
        </>
    );
};

export default LandingPage;
